import type { ConversationSettingsKey } from '@adieuu/shared';

type Translate = (key: string, options?: Record<string, unknown>) => string;

const SETTING_LABELS: Record<ConversationSettingsKey, { key: string; defaultValue: string }> = {
  gifs_disabled: {
    key: 'gif.conversationDisabledByAdmin',
    defaultValue: 'Disable GIFs for this conversation',
  },
  gif_content_filter: {
    key: 'gif.contentFilterTitle',
    defaultValue: 'GIF/Sticker content filter',
  },
  custom_emojis_disabled: {
    key: 'customEmoji.conversationDisabledByAdmin',
    defaultValue: 'Disable custom emojis',
  },
  persistent_search_cache_disallowed: {
    key: 'conversations.messageSearch.settingsDisallowTitle',
    defaultValue: 'Disallow persistent local search cache',
  },
  allow_skip_moderation: {
    key: 'conversations.allowSkipModeration',
    defaultValue: 'Allow members to skip moderation',
  },
  allow_audio_calls: {
    key: 'conversations.audioCallsEnabled',
    defaultValue: 'Allow audio calls',
  },
  allow_video_calls: {
    key: 'conversations.videoCallsEnabled',
    defaultValue: 'Allow video calls',
  },
  allow_screenshare: {
    key: 'conversations.screenshareEnabled',
    defaultValue: 'Allow screen sharing',
  },
};

const VALUE_LABELS: Record<string, { key: string; defaultValue: string }> = {
  enabled: {
    key: 'conversations.systemMessage.settingValueEnabled',
    defaultValue: 'enabled',
  },
  disabled: {
    key: 'conversations.systemMessage.settingValueDisabled',
    defaultValue: 'disabled',
  },
  off: { key: 'gif.contentFilterOff', defaultValue: 'Off' },
  low: { key: 'gif.contentFilterLow', defaultValue: 'Low' },
  medium: { key: 'gif.contentFilterMedium', defaultValue: 'Medium' },
  high: { key: 'gif.contentFilterHigh', defaultValue: 'High' },
};

export function formatConversationSettingsAnnouncement(
  t: Translate,
  actor: string,
  setting: string | undefined,
  value: string | undefined,
): string {
  const settingMeta = setting
    ? SETTING_LABELS[setting as ConversationSettingsKey]
    : undefined;
  const valueMeta = value ? VALUE_LABELS[value] : undefined;
  if (!settingMeta || !valueMeta) {
    return t('conversations.systemMessage.settingsUpdatedGeneric', {
      actor,
      defaultValue: `${actor} updated the conversation settings`,
    });
  }
  const settingLabel = t(settingMeta.key, { defaultValue: settingMeta.defaultValue });
  const valueLabel = t(valueMeta.key, { defaultValue: valueMeta.defaultValue });
  return t('conversations.systemMessage.settingsUpdated', {
    actor,
    setting: settingLabel,
    value: valueLabel,
    defaultValue: `${actor} set '${settingLabel}' to ${valueLabel}`,
  });
}
